import type { ComponentPublicInstance } from 'vue'
import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
import {
  AdditionalBenefitType,
  BenefitCategory,
  PlanBenefitType
} from '~/generated/api-clients-generated'
import { ProfileStore } from '~/stores/profile'

export interface HighlightRow {
  id: string
  label: string
  icon: string
  categoryIds?: readonly BenefitCategory[]
  abo?: AdditionalBenefitType
  planBenefitType?: PlanBenefitType
}

export type BenefitDetail = { categoryId?: number; name?: string | null; description: string }

const FLOW_TO_DRUG_BENEFIT_DEDUP_KEY: Partial<Record<AdditionalBenefitType, string>> = {
  [AdditionalBenefitType.OtcMedications]: 'otc'
}

const DEFAULT_FALLBACK_BENEFIT_TYPES = [
  AdditionalBenefitType.Vision,
  AdditionalBenefitType.Dental,
  AdditionalBenefitType.Fitness,
  AdditionalBenefitType.OtcMedications,
  AdditionalBenefitType.Hearing
] as const

export class BenefitHighlights {
  static async use() {
    const { getComponentContent } = Cms.use()
    const { content } = await getComponentContent('QuoteCardSectionBenefitHighlights')
    const profile = ProfileStore.use()
    const { additionalBenefitOptions } = await AdditionalBenefits.use('compact')
    const { benefitMap } = await BenefitDetailsMap.use()
    const { drugDerivedHighlights } = DrugHighlightMap.use()

    const MAX_HIGHLIGHTS = computed(() => content.value?.data?.maxHighlights)
    const MIN_HIGHLIGHTS = computed(() => content.value?.data?.minHighlights)

    const flowSelectedBenefits = computed(() =>
      additionalBenefitOptions.value.map((abo) => ({
        ...abo,
        requested: profile.needsAssessment.additionalBenefits?.includes(abo.value),
        text: abo.value === AdditionalBenefitType.OtcMedications ? 'Over the Counter' : abo.text
      }))
    )

    const flowBenefitToRow = (b: (typeof flowSelectedBenefits.value)[number]): HighlightRow => ({
      id: `flow-${b.value}`,
      label: b.text ?? '',
      icon: b.icon ?? '',
      abo: b.value,
      planBenefitType: b.planBenefitType
    })

    const allHighlightRows = computed((): HighlightRow[] => {
      const requestedFlowBenefits = flowSelectedBenefits.value.filter((b) => b.requested)
      const flowHighlights: HighlightRow[] = requestedFlowBenefits.map(flowBenefitToRow)

      if (flowHighlights.length >= MAX_HIGHLIGHTS.value)
        return flowHighlights.slice(0, MAX_HIGHLIGHTS.value)

      const flowedDrugDedupKeys = new Set(
        requestedFlowBenefits
          .map((b) => FLOW_TO_DRUG_BENEFIT_DEDUP_KEY[b.value])
          .filter((k): k is string => !!k)
      )

      const drugHighlights: HighlightRow[] = drugDerivedHighlights.value
        .filter((h) => !flowedDrugDedupKeys.has(h.key))
        .slice(0, MAX_HIGHLIGHTS.value - flowHighlights.length)
        .map((h) => ({
          id: `drug-${h.key}`,
          label: h.label,
          icon: h.icon,
          categoryIds: h.categoryIds
        }))

      const result = [...flowHighlights, ...drugHighlights]

      if (result.length < MIN_HIGHLIGHTS.value) {
        const existingIds = new Set(result.map((h) => h.id))
        for (const benefitType of DEFAULT_FALLBACK_BENEFIT_TYPES) {
          if (result.length >= MIN_HIGHLIGHTS.value) break
          const flowId = `flow-${benefitType}`
          if (existingIds.has(flowId)) continue
          const drugKey = FLOW_TO_DRUG_BENEFIT_DEDUP_KEY[benefitType]
          if (drugKey && existingIds.has(`drug-${drugKey}`)) continue
          const b = flowSelectedBenefits.value.find((b) => b.value === benefitType)
          if (b) result.push(flowBenefitToRow(b))
        }
      }

      return result
    })

    const getDetailsForPlan = (plan: QuotedPlanVm, row: HighlightRow): BenefitDetail[] => {
      if (row.categoryIds) {
        const matched: BenefitDetail[] = []
        for (const section of plan.details?.curatedBenefits ?? []) {
          for (const benefit of section.benefits ?? []) {
            if (benefit.categoryId != null && row.categoryIds.includes(benefit.categoryId)) {
              matched.push({
                categoryId: benefit.categoryId as number,
                name: benefit.name,
                description: BenefitDetailPrettifier.prettify(benefit.description ?? '') ?? ''
              })
            }
          }
        }
        return matched
      }

      if (row.abo !== undefined) {
        const details = benefitMap.value.find((b) => b.id === row.abo)?.getter(plan)
        return (details ?? []).map((d) => ({
          categoryId: d.categoryId as number | undefined,
          name: d.name,
          description: BenefitDetailPrettifier.prettify(d.description ?? '') ?? ''
        }))
      }

      return []
    }

    const isPlanBenefitCovered = (plan: QuotedPlanVm, row: HighlightRow): boolean => {
      if (row.categoryIds) {
        return getDetailsForPlan(plan, row).length > 0
      }
      if (row.planBenefitType !== undefined) {
        return !!plan.details.selectedBenefits?.[PlanBenefitType[row.planBenefitType] as any]
      }
      return false
    }

    return { allHighlightRows, getDetailsForPlan, isPlanBenefitCovered, content }
  }

  // Corrects bug when description is over the character count (causing the "See more" link
  // to show), but fits fully within the line-clamp threshold therefore not needing the link.
  static useClampDetection<K extends string | number>() {
    const visuallyClamped = reactive<Record<string | number, boolean>>({})

    const clampDetectionRef = (el: Element | ComponentPublicInstance | null, key: K) => {
      if (!el || !(el instanceof Element)) return
      nextTick(() => {
        visuallyClamped[key] = el.scrollHeight > el.clientHeight
      })
    }

    return { visuallyClamped, clampDetectionRef }
  }
}
